import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { INDEX_MINT, INDEX_SYMBOL } from "../lib/data";
import {
  DEFAULTS,
  fetchSiMarket,
  fmtPct,
  fmtUsd,
  projectYield,
} from "../lib/yield";

function Field({ id, label, hint, children }) {
  return (
    <div className="field yield-field">
      <label htmlFor={id}>
        {label}
        {hint ? <span className="yield-hint">{hint}</span> : null}
      </label>
      {children}
    </div>
  );
}

function Slider({ id, value, onChange, min, max, step = 1, suffix = "%" }) {
  return (
    <div className="yield-slider">
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <strong className="mono">
        {value}
        {suffix}
      </strong>
    </div>
  );
}

export default function YieldPage({ data }) {
  const schedule = data?.schedule || {};
  const lottery = data?.lottery || {};
  const spend = data?.spend || {};

  const [volume24hUsd, setVolume24hUsd] = useState(DEFAULTS.volume24hUsd);
  const [priceUsd, setPriceUsd] = useState(DEFAULTS.priceUsd);
  const [marketCapUsd, setMarketCapUsd] = useState(DEFAULTS.marketCapUsd);
  const [myTokens, setMyTokens] = useState(DEFAULTS.myTokens);
  const [creatorFeePct, setCreatorFeePct] = useState(DEFAULTS.creatorFeePct);
  const [lotteryPct, setLotteryPct] = useState(
    lottery.lotteryPct ?? schedule.lotteryPct ?? DEFAULTS.lotteryPct,
  );
  const [ataDragPct, setAtaDragPct] = useState(DEFAULTS.ataDragPct);
  const [retainPct, setRetainPct] = useState(DEFAULTS.retainPct);
  const [eligibleHolders, setEligibleHolders] = useState(
    spend.holderCount || lottery.lastWinner?.eligibleCount || DEFAULTS.eligibleHolders,
  );
  const [liveNote, setLiveNote] = useState(null);
  const [liveError, setLiveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchSiMarket(INDEX_MINT);
        if (cancelled || !m) return;
        if (m.volume24hUsd > 0) setVolume24hUsd(Math.round(m.volume24hUsd));
        if (m.priceUsd > 0) setPriceUsd(m.priceUsd);
        if (m.marketCapUsd > 0) setMarketCapUsd(m.marketCapUsd);
        setLiveNote(
          `Seeded from live StonkFun: 24h vol ${fmtUsd(m.volume24hUsd)} · mcap ${fmtUsd(m.marketCapUsd)}`,
        );
        setLiveError(null);
      } catch (e) {
        if (!cancelled) setLiveError(e.message || "Could not load live market");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const result = useMemo(
    () =>
      projectYield({
        volume24hUsd,
        priceUsd,
        marketCapUsd,
        myTokens,
        creatorFeePct,
        lotteryPct,
        ataDragPct,
        retainPct,
        eligibleHolders,
      }),
    [
      volume24hUsd,
      priceUsd,
      marketCapUsd,
      myTokens,
      creatorFeePct,
      lotteryPct,
      ataDragPct,
      retainPct,
      eligibleHolders,
    ],
  );

  const supplyUi = result.inputs.supplyUi;

  return (
    <section className="app-section" style={{ paddingTop: "2.5rem" }}>
      <div className="shell">
        <p className="section-kicker">
          <i className="brand-diamond" aria-hidden /> Yield
        </p>
        <h2 className="section-title">Fee → holder calculator</h2>
        <p className="section-lead left">
          {`Play with 24h $${INDEX_SYMBOL} volume and protocol knobs. Estimates
          creator fees → lottery skim → ATA/gas drag → treasury retain → pro-rata
          airdrop pool. Illustrative only — not APY guarantees.`}
        </p>

        {liveNote && <p className="yield-live">{liveNote}</p>}
        {liveError && <p className="portfolio-error">{liveError}</p>}

        <div className="yield-grid">
          <div className="panel yield-controls">
            <h3>Inputs</h3>

            <Field id="vol" label="24h volume (USD)" hint="StonkFun market">
              <input
                id="vol"
                type="number"
                min={0}
                step={1000}
                value={volume24hUsd}
                onChange={(e) => setVolume24hUsd(Number(e.target.value))}
              />
            </Field>

            <Field id="price" label={`$${INDEX_SYMBOL} price (USD)`}>
              <input
                id="price"
                type="number"
                min={0}
                step={0.000001}
                value={priceUsd}
                onChange={(e) => setPriceUsd(Number(e.target.value))}
              />
            </Field>

            <Field id="mcap" label="Market cap (USD)" hint="used to infer supply">
              <input
                id="mcap"
                type="number"
                min={0}
                step={1000}
                value={marketCapUsd}
                onChange={(e) => setMarketCapUsd(Number(e.target.value))}
              />
            </Field>

            <Field id="bag" label={`Your $${INDEX_SYMBOL} balance`}>
              <input
                id="bag"
                type="number"
                min={0}
                step={1000}
                value={myTokens}
                onChange={(e) => setMyTokens(Number(e.target.value))}
              />
            </Field>

            <Field id="holders" label="Eligible holders" hint="≥ min hold">
              <input
                id="holders"
                type="number"
                min={1}
                step={1}
                value={eligibleHolders}
                onChange={(e) => setEligibleHolders(Number(e.target.value))}
              />
            </Field>

            <Field id="fee" label="Creator fee share" hint="of volume">
              <Slider
                id="fee"
                value={creatorFeePct}
                onChange={setCreatorFeePct}
                min={0.5}
                max={2}
                step={0.1}
              />
            </Field>

            <Field id="lot" label="Lottery skim" hint="equal-odds pot">
              <Slider
                id="lot"
                value={lotteryPct}
                onChange={setLotteryPct}
                min={0}
                max={30}
                step={1}
              />
            </Field>

            <Field id="ata" label="ATA & gas drag" hint="rent / priority fees">
              <Slider
                id="ata"
                value={ataDragPct}
                onChange={setAtaDragPct}
                min={0}
                max={40}
                step={1}
              />
            </Field>

            <Field id="ret" label="Treasury retain" hint="reserve + next cycles">
              <Slider
                id="ret"
                value={retainPct}
                onChange={setRetainPct}
                min={0}
                max={70}
                step={5}
              />
            </Field>
          </div>

          <div className="yield-results">
            <div className="portfolio-summary" style={{ marginTop: 0 }}>
              <div className="portfolio-stat">
                <div className="label">Est. APY (airdrop)</div>
                <strong>{fmtPct(result.apyPct)}</strong>
                <p>Pro-rata board buys only</p>
              </div>
              <div className="portfolio-stat">
                <div className="label">+ lottery EV</div>
                <strong>{fmtPct(result.combinedApyPct)}</strong>
                <p>Airdrop + equal-odds EV</p>
              </div>
              <div className="portfolio-stat">
                <div className="label">Your daily</div>
                <strong>{fmtUsd(result.myDailyUsd, 4)}</strong>
                <p>From holder pool</p>
              </div>
              <div className="portfolio-stat">
                <div className="label">Your bag</div>
                <strong>{fmtUsd(result.myBagUsd)}</strong>
                <p>
                  {myTokens.toLocaleString()} {`$${INDEX_SYMBOL}`}
                </p>
              </div>
            </div>

            <div className="panel">
              <h3>Fee waterfall (per day)</h3>
              <div className="stat-row">
                <span>24h volume</span>
                <strong>{fmtUsd(volume24hUsd, 0)}</strong>
              </div>
              <div className="stat-row">
                <span>Gross creator fees ({creatorFeePct}%)</span>
                <strong>{fmtUsd(result.grossFeesUsd)}</strong>
              </div>
              <div className="stat-row">
                <span>− Lottery ({lotteryPct}%)</span>
                <strong>{fmtUsd(result.lotteryUsd)}</strong>
              </div>
              <div className="stat-row">
                <span>− ATA & gas ({ataDragPct}%)</span>
                <strong>{fmtUsd(result.ataUsd)}</strong>
              </div>
              <div className="stat-row">
                <span>− Treasury retain ({retainPct}%)</span>
                <strong>{fmtUsd(result.retainUsd)}</strong>
              </div>
              <div className="stat-row yield-stat-emph">
                <span>Holder airdrop pool</span>
                <strong>{fmtUsd(result.holderPoolUsd)}</strong>
              </div>
              <div className="stat-row">
                <span>Your supply share</span>
                <strong>{fmtPct(result.sharePct, 4)}</strong>
              </div>
              <div className="stat-row">
                <span>Inferred supply</span>
                <strong>{Math.round(supplyUi).toLocaleString()}</strong>
              </div>
              <div className="stat-row">
                <span>Lottery EV / holder / day</span>
                <strong>{fmtUsd(result.lotteryEvDailyUsd, 4)}</strong>
              </div>
            </div>

            <p className="portfolio-footnote">
              Assumes steady volume, successful swaps, and that you stay above the
              min hold. ATA drag is an estimate (live rent is capped at{" "}
              {schedule.ataMaxPct ?? 40}% of each cycle). Lottery is equal-odds,
              not bag-weighted.{" "}
              <Link href="/portfolio">Check your ATAs</Link>
              {" · "}
              <Link href="/docs">How fees work</Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
